import { inject, Plugin, render } from 'vue'
import type { App } from 'vue'
import { defu } from 'defu'
import ConfirmDialog from './ConfirmDialog.vue'
import type { ConfirmDialogOptions } from './utils'
import { mount } from './utils'
import { ConfirmDialogKey } from '~/lib/symbols'
import type { TranslateFn } from '~/plugins/vue-plugins/i18n'

declare module '~/types/vue-shim' {
  interface AppGlobalProperties {
    $confirm: (options: ConfirmDialogOptions) => Promise<boolean>
  }
}

export type ConfirmDialogHandler = {
  (options: ConfirmDialogOptions): Promise<boolean>
  delete(options?: ConfirmDialogOptions): Promise<boolean>
}
export function createConfirmDialogHandler(
  app: App<any>,
  t: TranslateFn,
  globalOptions?: ConfirmDialogOptions,
): ConfirmDialogHandler {
  const confirm = async function (options: ConfirmDialogOptions) {
    const decision = await new Promise<boolean>((resolve) => {
      mount(
        ConfirmDialog,
        {
          ...defu(options, globalOptions ?? {}),
          resolve,
        },
        app,
      )
    })
    render(null, app._container.firstElementChild)
    return decision
  }

  confirm.delete = (options?: ConfirmDialogOptions) =>
    confirm({
      content: t('ConfirmDialogTitleDelete'),
      confirmationText: t('ButtonDelete'),
      confirmationButtonProps: {
        color: 'error',
      },
      ...options,
    })

  return confirm
}

export const ConfirmDialogPlugin: Plugin<[TranslateFn, ConfirmDialogOptions]> = {
  install(app, t, globalOptions?: ConfirmDialogOptions) {
    const handler = createConfirmDialogHandler(app, t, globalOptions)
    app.provide(ConfirmDialogKey, handler)
    app.config.globalProperties.$confirm = handler
  },
}

export function useConfirm(): ConfirmDialogHandler {
  const confirm = inject(ConfirmDialogKey)
  if (!confirm) {
    throw new Error('No ConfirmDialog provided!')
  }
  return confirm
}
